import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Search, X, ArrowUpRight } from "lucide-react";
import { searchSite } from "@/lib/search";

/**
 * SiteSearch: a compact, keyboard-friendly search bar with live preview
 * results. Sits in the header. Users can:
 *   • type any keyword and see grouped matches instantly
 *   • use ↑/↓ to move through the previews and Enter to open
 *   • press Esc to close, or click outside
 *
 * Variant `compact` fits the desktop header; the dropdown expands below it.
 */
export function SiteSearch({ variant = "compact", className = "", onNavigate }) {
    const { t } = useTranslation();
    const [query, setQuery] = useState("");
    const [open, setOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState(0);
    const rootRef = useRef(null);
    const inputRef = useRef(null);
    const navigate = useNavigate();

    const results = useMemo(() => searchSite(query, 10), [query]);

    // Group results by their `group` field, preserving score order
    const grouped = useMemo(() => {
        const map = new Map();
        for (const r of results) {
            if (!map.has(r.group)) map.set(r.group, []);
            map.get(r.group).push(r);
        }
        return Array.from(map.entries());
    }, [results]);

    // Close on outside click
    useEffect(() => {
        function handler(e) {
            if (rootRef.current && !rootRef.current.contains(e.target)) {
                setOpen(false);
            }
        }
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    // Global keyboard shortcut: ⌘K / Ctrl+K focuses the search input
    useEffect(() => {
        function onKey(e) {
            if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
                e.preventDefault();
                inputRef.current?.focus();
                setOpen(true);
            }
        }
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, []);

    // Reset active preview when query changes
    useEffect(() => {
        setActiveIndex(0);
    }, [query]);

    function handleSelect(item) {
        setOpen(false);
        setQuery("");
        if (onNavigate) onNavigate();
        navigate(item.to);
    }

    function onKeyDown(e) {
        if (!open) return;
        if (e.key === "ArrowDown") {
            e.preventDefault();
            setActiveIndex((i) => Math.min(i + 1, Math.max(results.length - 1, 0)));
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setActiveIndex((i) => Math.max(i - 1, 0));
        } else if (e.key === "Enter") {
            e.preventDefault();
            if (results[activeIndex]) handleSelect(results[activeIndex]);
        } else if (e.key === "Escape") {
            setOpen(false);
            inputRef.current?.blur();
        }
    }

    const wrapperCls =
        variant === "compact"
            ? "relative w-full max-w-md hidden md:block"
            : "relative w-full";

    return (
        <div ref={rootRef} className={`${wrapperCls} ${className}`} data-testid="site-search">
            <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[color:var(--text-soft)] pointer-events-none" />
                <input
                    ref={inputRef}
                    type="text"
                    value={query}
                    onChange={(e) => {
                        setQuery(e.target.value);
                        setOpen(true);
                    }}
                    onFocus={() => setOpen(true)}
                    onKeyDown={onKeyDown}
                    placeholder={t("search.placeholder")}
                    className="w-full h-10 pl-9 pr-9 rounded-xl border border-[color:var(--border-default)] bg-white/90 text-sm text-[color:var(--brand-navy)] placeholder:text-[color:var(--text-soft)] focus:outline-none focus:ring-2 focus:ring-[color:var(--brand-teal-2)] focus:border-transparent"
                    aria-label={t("search.ariaLabel")}
                    aria-expanded={open}
                    aria-controls="site-search-listbox"
                    data-testid="site-search-input"
                />
                {query && (
                    <button
                        type="button"
                        onClick={() => {
                            setQuery("");
                            inputRef.current?.focus();
                        }}
                        aria-label={t("search.clearSearch")}
                        className="absolute right-2 top-1/2 -translate-y-1/2 rounded-lg p-1 text-[color:var(--text-soft)] hover:bg-[color:var(--bg-subtle)]"
                    >
                        <X className="h-4 w-4" />
                    </button>
                )}
                {!query && (
                    <kbd className="hidden md:inline-flex absolute right-2 top-1/2 -translate-y-1/2 items-center rounded-md border border-[color:var(--border-default)] bg-[color:var(--bg-subtle)] px-1.5 py-0.5 text-[10px] font-mono text-[color:var(--text-soft)]">
                        ⌘K
                    </kbd>
                )}
            </div>

            {/* Results dropdown */}
            {open && query.trim() && (
                <div
                    id="site-search-listbox"
                    role="listbox"
                    className="absolute z-50 mt-2 left-0 right-0 max-h-[70vh] overflow-y-auto rounded-2xl border border-[color:var(--border-default)] bg-white shadow-xl"
                    style={{ boxShadow: "0 20px 60px rgba(15,39,71,0.18)" }}
                    data-testid="site-search-results"
                >
                    {results.length === 0 ? (
                        <div className="p-4 text-sm text-[color:var(--text-muted)]">
                            {t("search.noResultsPre")} <span className="font-semibold text-[color:var(--brand-navy)]">“{query}”</span>{t("search.noResultsPost")}
                        </div>
                    ) : (
                        <div className="py-2">
                            {grouped.map(([groupName, items]) => (
                                <div key={groupName} className="pt-1">
                                    <div className="px-4 pt-2 pb-1 text-[10px] uppercase tracking-wider font-semibold text-[color:var(--text-soft)]">
                                        {groupName}
                                    </div>
                                    <ul>
                                        {items.map((r) => {
                                            const idx = results.indexOf(r);
                                            const isActive = idx === activeIndex;
                                            return (
                                                <li key={r.id}>
                                                    <button
                                                        type="button"
                                                        onMouseEnter={() => setActiveIndex(idx)}
                                                        onClick={() => handleSelect(r)}
                                                        className={`w-full text-left px-4 py-2.5 flex items-start gap-3 transition-colors ${
                                                            isActive
                                                                ? "bg-[color:var(--brand-teal-soft)]"
                                                                : "hover:bg-[color:var(--bg-subtle)]"
                                                        }`}
                                                        role="option"
                                                        aria-selected={isActive}
                                                        data-testid={`site-search-result-${r.id}`}
                                                    >
                                                        <div className="flex-1 min-w-0">
                                                            <p className="text-sm font-semibold text-[color:var(--brand-navy)] truncate">
                                                                <Highlight text={r.title} query={query} />
                                                            </p>
                                                            {r.subtitle && (
                                                                <p className="mt-0.5 text-xs text-[color:var(--text-muted)] leading-snug line-clamp-2">
                                                                    <Highlight text={r.subtitle} query={query} />
                                                                </p>
                                                            )}
                                                        </div>
                                                        <ArrowUpRight
                                                            className={`h-4 w-4 shrink-0 mt-0.5 ${
                                                                isActive
                                                                    ? "text-[color:var(--brand-teal-2)]"
                                                                    : "text-[color:var(--text-soft)]"
                                                            }`}
                                                        />
                                                    </button>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                </div>
                            ))}
                            <div className="px-4 py-2 border-t border-[color:var(--border-default)] mt-1 text-[11px] text-[color:var(--text-soft)] flex flex-wrap gap-x-4 gap-y-1">
                                <span><kbd className="px-1 bg-[color:var(--bg-subtle)] rounded">↑↓</kbd> {t("search.navigate")}</span>
                                <span><kbd className="px-1 bg-[color:var(--bg-subtle)] rounded">↲</kbd> {t("search.open")}</span>
                                <span><kbd className="px-1 bg-[color:var(--bg-subtle)] rounded">esc</kbd> {t("search.close")}</span>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

function Highlight({ text, query }) {
    if (!query) return <>{text}</>;
    const q = query.trim().toLowerCase();
    const tokens = q.split(/\s+/).filter(Boolean);
    if (tokens.length === 0) return <>{text}</>;

    const pattern = new RegExp(`(${tokens.map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|")})`, "ig");
    const parts = text.split(pattern);
    return (
        <>
            {parts.map((p, i) =>
                pattern.test(p) ? (
                    <mark key={i} className="bg-[color:var(--brand-teal-soft)] text-[color:var(--brand-navy)] rounded px-0.5">
                        {p}
                    </mark>
                ) : (
                    <React.Fragment key={i}>{p}</React.Fragment>
                )
            )}
        </>
    );
}
